// @djsem/analysis-contract — Lyric / Typography Engine (Prompt 13R).
// Turns P13 transcript artifacts into typography moments and lanes. NEVER invents lyrics
// (rule 5); rights-aware (restricted ⇒ no public export); Brand World application changes
// STYLE tokens only, never text. Manual correction supported.

import type { DJSEMTranscriptArtifact } from "./transcription";
import { SeqCounter } from "@djsem/shared";
import { deterministicHash } from "./prepWorkspace";
import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** A single word within a lyric line, with optional timing and confidence. */
export interface DJSEMLyricWord { text: string; startTimeMs?: number; endTimeMs?: number; confidence: number; }
/** A single line of lyrics, transcript-derived or manually entered. */
export interface DJSEMLyricLine {
  id: string; text: string; words: DJSEMLyricWord[];
  startTimeMs?: number; endTimeMs?: number; confidence: number;
  source: "transcript" | "manual"; warnings: string[];
}

/** How a typography moment presents its source text (truncated phrase, poetic break, full line, or repeated hook). */
export type DJSEMTypographyMode = "key_phrase" | "poetic_fragment" | "full_line" | "hook_repeat";

/** A single on-screen typography moment derived from lyric lines. */
export interface DJSEMTypographyMoment {
  id: string; mode: DJSEMTypographyMode; text: string;
  startTimeMs?: number; endTimeMs?: number;
  sourceLineIds: string[]; confidence: number;
  styleTokens: Record<string, string>; warnings: string[];
}

/** A track's full sequence of typography moments, with rights-gated public-export eligibility. */
export interface DJSEMTypographyLane {
  laneId: string; trackId: string;
  moments: DJSEMTypographyMoment[];
  rightsStatus: "unknown" | "owned" | "licensed" | "restricted";
  canDisplayInPublicExport: boolean;
  warnings: string[]; createdAt: string;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link LyricTypographyService.normalizeTranscript}. */
export interface NormalizeTranscriptInput { artifact: DJSEMTranscriptArtifact; }
/** Input for {@link LyricTypographyService.groupLinesIntoPhrases}. */
export interface GroupLyricLinesInput { lines: DJSEMLyricLine[]; maxGapMs?: number; }
/** Input for {@link LyricTypographyService.detectHookPhrases}. */
export interface DetectHookPhrasesInput { lines: DJSEMLyricLine[]; minRepeats?: number; }
/** Input for {@link LyricTypographyService.createTypographyMoment}. */
export interface CreateTypographyMomentInput { mode: DJSEMTypographyMode; text: string; sourceLineIds?: string[]; startTimeMs?: number; endTimeMs?: number; confidence?: number; }
/** Input for {@link LyricTypographyService.buildTypographyLane}. */
export interface BuildTypographyLaneInput { trackId: string; artifact: DJSEMTranscriptArtifact; moments: DJSEMTypographyMoment[]; createdAt?: string; }
/** Input for {@link LyricTypographyService.applyBrandWorld}. */
export interface ApplyBrandTypographyInput { lane: DJSEMTypographyLane; brand: { fontFamily?: string; primaryColor?: string; styleTags?: string[] }; }

const clampUnit = (n: number | undefined, d = 0.5) => Math.max(0, Math.min(1, typeof n === "number" && Number.isFinite(n) ? n : d));
const _seq = new SeqCounter();

// ---- service (class-based) --------------------------------------------------

/**
 * Turns transcript artifacts into typography moments and lanes. Never invents lyrics, is
 * rights-aware (restricted rights block public export), and Brand World application only
 * changes style tokens, never text.
 */
export class LyricTypographyService {
  /**
   * Convert a transcript artifact's segments into lyric lines with per-word timing (uniformly distributed, never invented beyond the segment window).
   * @param input - the transcript artifact to normalize.
   * @returns the constructed lyric lines.
   */
  normalizeTranscript(input: NormalizeTranscriptInput): DJSEMLyricLine[] {
    return input.artifact.segments.map((s) => ({
      id: `line_${s.id}`, text: s.text,
      // Word timings: derived only if segment has real timing; distributed uniformly with
      // reduced confidence — never invented beyond the segment window.
      words: this.splitWords(s.text, s.startTimeMs, s.endTimeMs, s.confidence),
      startTimeMs: s.startTimeMs, endTimeMs: s.endTimeMs,
      confidence: clampUnit(s.confidence), source: "transcript", warnings: [...s.warnings],
    }));
  }

  /**
   * Create a manually-entered lyric line (full confidence, source "manual").
   * @param input - the line's text and optional timing.
   * @returns the constructed lyric line.
   */
  createManualLine(input: { text: string; startTimeMs?: number; endTimeMs?: number }): DJSEMLyricLine {
    return { id: `line_manual_${_seq.next()}`, text: input.text, words: this.splitWords(input.text, input.startTimeMs, input.endTimeMs, 1), startTimeMs: input.startTimeMs, endTimeMs: input.endTimeMs, confidence: 1, source: "manual", warnings: [] };
  }

  /**
   * Merge adjacent lyric lines into phrases when their time gap is within the threshold.
   * @param input - the lines to group and an optional max-gap override (default 1500ms).
   * @returns the grouped lines.
   */
  groupLinesIntoPhrases(input: GroupLyricLinesInput): DJSEMLyricLine[] {
    const gap = input.maxGapMs ?? 1500;
    const out: DJSEMLyricLine[] = [];
    for (const line of input.lines) {
      const last = out[out.length - 1];
      if (last && typeof last.endTimeMs === "number" && typeof line.startTimeMs === "number" && line.startTimeMs - last.endTimeMs <= gap) {
        out[out.length - 1] = {
          ...last, id: last.id, text: `${last.text} ${line.text}`.trim(),
          words: [...last.words, ...line.words], endTimeMs: line.endTimeMs,
          confidence: Math.min(last.confidence, line.confidence),
          warnings: [...last.warnings, ...line.warnings],
        };
      } else out.push({ ...line });
    }
    return out;
  }

  /**
   * Detect repeated lines (hooks) that occur at least the minimum number of times.
   * @param input - the lines to scan and an optional minimum repeat count (default 2).
   * @returns a hook_repeat typography moment for each detected hook.
   */
  detectHookPhrases(input: DetectHookPhrasesInput): DJSEMTypographyMoment[] {
    const minRepeats = input.minRepeats ?? 2;
    const counts = new Map<string, DJSEMLyricLine[]>();
    for (const l of input.lines) {
      const key = l.text.trim().toLowerCase();
      if (!key) continue;
      counts.set(key, [...(counts.get(key) ?? []), l]);
    }
    const hooks: DJSEMTypographyMoment[] = [];
    for (const [, lines] of counts) {
      if (lines.length < minRepeats) continue;
      const first = lines[0];
      hooks.push({
        id: `hook_${_seq.next()}`, mode: "hook_repeat", text: first.text,
        startTimeMs: first.startTimeMs, endTimeMs: first.endTimeMs,
        sourceLineIds: lines.map((l) => l.id),
        confidence: clampUnit(Math.min(...lines.map((l) => l.confidence)) + 0.1),
        styleTokens: {}, warnings: [],
      });
    }
    return hooks;
  }

  /**
   * Create a typography moment from given text, truncating (never inventing words) per mode rules.
   * @param input - the mode, source text, optional source line ids/timing/confidence.
   * @returns the constructed typography moment.
   */
  createTypographyMoment(input: CreateTypographyMomentInput): DJSEMTypographyMoment {
    const warnings: string[] = [];
    let text = input.text;
    if (input.mode === "key_phrase") {
      // Key phrase = at most 5 words from the given text (never adds words).
      const words = text.split(/\s+/).filter(Boolean);
      if (words.length > 5) { text = words.slice(0, 5).join(" "); warnings.push("Key phrase truncated to 5 words."); }
    }
    if (input.mode === "poetic_fragment") {
      // Poetic fragment = subset of the given words, line-broken; still never invents words.
      text = text.split(/\s+/).filter(Boolean).slice(0, 8).join("\n");
    }
    return {
      id: `tm_${_seq.next()}`, mode: input.mode, text,
      startTimeMs: input.startTimeMs, endTimeMs: input.endTimeMs,
      sourceLineIds: input.sourceLineIds ?? [], confidence: clampUnit(input.confidence, 0.8),
      styleTokens: {}, warnings,
    };
  }

  /**
   * Assemble a track's typography lane from its moments, gating public-export eligibility by the transcript's rights status.
   * @param input - the track id, source transcript artifact (for rights status), and moments.
   * @returns the constructed typography lane, sorted by start time.
   */
  buildTypographyLane(input: BuildTypographyLaneInput): DJSEMTypographyLane {
    const rights = input.artifact.rightsStatus;
    const warnings: string[] = [];
    if (rights === "unknown") warnings.push("Lyric rights unknown; lane not cleared for public export.");
    if (rights === "restricted") warnings.push("Lyric rights restricted; public export blocked.");
    return {
      laneId: `lane_${input.trackId}_${deterministicHash(input.moments.map((m) => m.id).sort())}`,
      trackId: input.trackId,
      moments: [...input.moments].sort((a, b) => (a.startTimeMs ?? 0) - (b.startTimeMs ?? 0) || a.id.localeCompare(b.id)),
      rightsStatus: rights,
      canDisplayInPublicExport: rights === "owned" || rights === "licensed",
      warnings, createdAt: input.createdAt ?? "1970-01-01T00:00:00.000Z",
    };
  }

  /**
   * Apply Brand World style tokens (font/color/style tags) to a lane's moments. Text is always copied verbatim.
   * @param input - the lane and the brand's style properties.
   * @returns a new lane with updated styleTokens on each moment.
   */
  applyBrandWorld(input: ApplyBrandTypographyInput): DJSEMTypographyLane {
    const tokens: Record<string, string> = {};
    if (input.brand.fontFamily) tokens.fontFamily = input.brand.fontFamily;
    if (input.brand.primaryColor) tokens.color = input.brand.primaryColor;
    if (input.brand.styleTags?.length) tokens.styleTags = input.brand.styleTags.join(",");
    return {
      ...input.lane,
      // STYLE only — text is copied verbatim, never altered by brand application.
      moments: input.lane.moments.map((m) => ({ ...m, styleTokens: { ...m.styleTokens, ...tokens } })),
    };
  }

  /**
   * Validate a typography lane's timing consistency, confidence bounds, and rights/export-eligibility rule.
   * @param lane - the lane to validate.
   * @returns validation result.
   */
  validateTypographyLane(lane: DJSEMTypographyLane): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!lane?.laneId || !lane?.trackId) issues.push({ path: "$", message: "laneId and trackId required" });
    for (const m of lane?.moments ?? []) {
      if (typeof m.startTimeMs === "number" && typeof m.endTimeMs === "number" && m.startTimeMs > m.endTimeMs)
        issues.push({ path: `$.moments.${m.id}`, message: "startTimeMs must be <= endTimeMs" });
      if (!(m.confidence >= 0 && m.confidence <= 1)) issues.push({ path: `$.moments.${m.id}`, message: "confidence must be 0..1" });
    }
    if (lane?.rightsStatus === "restricted" && lane?.canDisplayInPublicExport)
      issues.push({ path: "$.canDisplayInPublicExport", message: "restricted rights cannot allow public export" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "typography_lane");
  }

  /**
   * Check whether a lane's lyric rights permit public export.
   * @param input - the lane to check.
   * @returns validation result (invalid when rights are restricted).
   */
  checkLyricRights(input: DJSEMTypographyLane): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (input.rightsStatus === "restricted") issues.push({ path: "$.rightsStatus", message: "restricted lyric rights — public export blocked" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "lyric_rights");
  }

  private splitWords(text: string, startMs?: number, endMs?: number, conf = 0.5): DJSEMLyricWord[] {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0) return [];
    if (typeof startMs !== "number" || typeof endMs !== "number" || endMs <= startMs) {
      return words.map((w) => ({ text: w, confidence: clampUnit(conf * 0.8) }));
    }
    const step = (endMs - startMs) / words.length;
    return words.map((w, i) => ({
      text: w, startTimeMs: Math.round(startMs + i * step), endTimeMs: Math.round(startMs + (i + 1) * step),
      confidence: clampUnit(conf * 0.8), // uniform distribution → reduced confidence, honest
    }));
  }
}
